import { useState } from "react";
import { ChevronDown, ChevronUp, Star, Clock, Compass, ExternalLink } from "lucide-react";
import { HeaderAccessory } from "@/components/cards/_pills";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AMBER = "#F59E0B";

function ProviderPill({ provider }) {
    if (!provider || provider === "raw" || provider === "web_search") return null;
    const label =
        provider === "viator" ? "Live · Viator"
            : provider === "klook" ? "Live · Klook"
            : `Live · ${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
    return (
        <span
            data-testid="experiences-provider-pill"
            data-provider={provider}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#737373]"
            title={`Affiliate-tracked via ${provider}`}
        >
            <span className="relative flex h-1.5 w-1.5">
                <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                    style={{ background: AMBER }}
                />
                <span
                    className="relative inline-flex h-1.5 w-1.5 rounded-full"
                    style={{ background: AMBER }}
                />
            </span>
            {label}
        </span>
    );
}

function fmtDuration(hours) {
    if (!hours) return "";
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m ? `${h}h ${m}m` : `${h}h`;
}

function ExperienceOptionCard({ option }) {
    const {
        id,
        title,
        provider,
        category,
        duration_hours,
        price_from,
        display_price,
        rating_score,
        image_url,
        short_description,
        book_url,
        source_label,
    } = option;
    const price = display_price || price_from;
    const target = book_url?.startsWith("/api/r/") ? `${BACKEND_URL}${book_url}` : book_url;
    const ctaLabel = source_label ? `Book on ${source_label}` : "Book Now";

    return (
        <article
            className="relative flex h-full w-[260px] flex-shrink-0 flex-col overflow-hidden rounded-xl border border-[#E5E5E5] bg-white"
            data-testid={`experience-option-${id}`}
        >
            <span
                aria-hidden
                className="absolute right-3 top-3 z-10 inline-block h-2.5 w-2.5 rounded-full ring-4"
                style={{ background: AMBER, boxShadow: `0 0 0 4px ${AMBER}26` }}
                data-testid="card-color-dot"
            />
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#F5F5F5]">
                {image_url ? (
                    <img
                        src={image_url}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.04]"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#A3A3A3]">
                        <Compass size={26} strokeWidth={1.5} />
                    </div>
                )}
                {category && (
                    <span className="absolute left-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#0A0A0A] backdrop-blur">
                        {category}
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-3">
                <h3 className="line-clamp-2 text-[14px] font-semibold leading-snug tracking-tight text-[#0A0A0A]">
                    {title}
                </h3>
                {short_description && (
                    <p className="line-clamp-2 text-[11.5px] leading-relaxed text-[#737373]">
                        {short_description}
                    </p>
                )}
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#737373]">
                        {duration_hours > 0 && (
                            <span className="inline-flex items-center gap-1">
                                <Clock size={11} strokeWidth={2} />
                                {fmtDuration(duration_hours)}
                            </span>
                        )}
                        {rating_score && (
                            <span className="inline-flex items-center gap-1 text-[#0A0A0A]">
                                <Star size={11} strokeWidth={2.25} className="fill-[#F59E0B] text-[#F59E0B]" />
                                {rating_score.toFixed(1)}
                            </span>
                        )}
                    </div>
                    {price?.amount > 0 && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">From</div>
                            <div className="text-[14px] font-semibold tracking-tight text-[#0A0A0A]">
                                {price.currency} {Math.round(price.amount).toLocaleString()}
                            </div>
                        </div>
                    )}
                </div>
                <a
                    href={target}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid={`experience-book-${id}`}
                    data-provider={provider}
                    className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3 py-2 text-[12px] font-semibold text-white transition hover:bg-[#1F1F1F]"
                >
                    {ctaLabel}
                    <ExternalLink size={11} strokeWidth={2.25} />
                </a>
            </div>
        </article>
    );
}

export default function ExperiencesGroupCard({ card }) {
    const {
        title,
        city,
        options = [],
        destination_weather,
    } = card;
    const [expanded, setExpanded] = useState(false);
    const visible = expanded ? options : options.slice(0, 6);
    const hiddenCount = Math.max(options.length - 6, 0);
    const primaryProvider = options[0]?.provider;

    return (
        <section className="space-y-3" data-testid="experiences-group">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2">
                    <h2 className="text-[18px] font-semibold tracking-tight text-[#0A0A0A]">
                        {title || (city ? `Things to do in ${city}` : "Bookable experiences")}
                    </h2>
                    <ProviderPill provider={primaryProvider} />
                </div>
                <HeaderAccessory destinationWeather={destination_weather} dotColor={AMBER} />
            </div>

            <div className="-mx-1 overflow-x-auto pb-2">
                <div className="flex gap-3 px-1">
                    {visible.map((o) => (
                        <ExperienceOptionCard key={o.id} option={o} />
                    ))}
                </div>
            </div>

            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid="experiences-toggle"
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                >
                    {expanded ? (
                        <>
                            <ChevronUp size={14} strokeWidth={2.25} /> Hide extras
                        </>
                    ) : (
                        <>
                            <ChevronDown size={14} strokeWidth={2.25} /> More options (+{hiddenCount})
                        </>
                    )}
                </button>
            )}
        </section>
    );
}
